import React from 'react';
import { RefreshCw, Save } from 'lucide-react';
import { Card, CardSection, CardCompact, Badge, PlayabilityBadge, Button } from '../ui';
import { ShoppingButton } from '../ShoppingButton';
import { TrajectoryChart } from '../TrajectoryChart';

export function ComboDetailPanel({ head, shaft, stats, baseline, trajectoryData, baselineTrajectoryData, loadingTrajectory, onSave, saving }) {
  if (!stats || !baseline || !head || !shaft) {
    return (
      <Card padding="p-4 sm:p-5">
        <div className="text-zinc-500 text-center py-8">Select a combination to view details</div>
      </Card>
    );
  }
  const carryDelta = (stats.carry || 0) - (baseline.carry || 0);

  const getPlayabilityStatus = (score) => {
    if (score >= 80) return 'green';
    if (score >= 50) return 'yellow';
    return 'red';
  };

  // Helper function to open 2nd Swing search
  const searchOnSecondSwing = (searchQuery) => {
    const encodedQuery = encodeURIComponent(searchQuery);
    window.open(`https://www.2ndswing.com/search.aspx?search=${encodedQuery}`, '_blank');
  };

  return (
    <Card padding="p-4 sm:p-5" className="bg-card">
      {/* Trajectory Chart */}
      <div className="mb-4">
        {loadingTrajectory ? (
          <div className="h-48 bg-zinc-950 rounded-lg flex items-center justify-center">
            <RefreshCw className="animate-spin text-zinc-500" size={20} />
          </div>
        ) : (
          <TrajectoryChart
            trajectoryData={trajectoryData}
            baselineData={baselineTrajectoryData}
            carry={stats.carry || 0}
            maxHeight={stats.max_height || 0}
            baselineCarry={baseline.carry || 0}
            baselineLabel="Baseline"
            activeLabel={`${head.name || 'Head'} + ${shaft.name || 'Shaft'}`}
          />
        )}
      </div>

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-4">
        <div>
          <div className="text-[10px] sm:text-xs text-text-secondary uppercase mb-1">Selected Build</div>
          <div className="text-base sm:text-lg font-bold text-text-primary">{head.name || 'Unknown Head'}</div>
          <div className="text-xs sm:text-sm text-text-secondary">+ {shaft.name || 'Unknown Shaft'}</div>
        </div>
        <div className="sm:text-right flex sm:block items-center gap-3">
          <div className="text-2xl sm:text-3xl font-bold text-text-primary">{stats.carry || 0} <span className="text-sm text-text-muted">yds</span></div>
          <div className={`text-xs sm:text-sm font-mono ${carryDelta > 0 ? 'text-success' : carryDelta < 0 ? 'text-error' : 'text-text-muted'}`}>
            {carryDelta > 0 ? '+' : ''}{carryDelta} vs baseline
          </div>
        </div>
      </div>

      <CardSection className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <CardCompact>
          <div className="text-xs text-text-secondary uppercase mb-1">Carry</div>
          <div className="text-xl font-bold text-text-primary">{stats.carry || 0}</div>
          <div className="text-xs text-text-muted">yds</div>
        </CardCompact>
        <CardCompact>
          <div className="text-xs text-text-secondary uppercase mb-1">Apex</div>
          <div className="text-xl font-bold text-text-primary">{stats.max_height ? Math.round(stats.max_height * 3) : 0}</div>
          <div className="text-xs text-text-muted">ft</div>
        </CardCompact>
        <CardCompact>
          <div className="text-xs text-text-secondary uppercase mb-1">Spin</div>
          <div className="text-xl font-bold text-text-primary">{stats.spin || 0}</div>
          <div className="text-xs text-text-muted">rpm</div>
        </CardCompact>
        <CardCompact>
          <div className="text-xs text-text-secondary uppercase mb-1">Descent</div>
          <div className="text-xl font-bold text-text-primary">{stats.descent_angle ? stats.descent_angle.toFixed(1) : '--'}</div>
          <div className="text-xs text-text-muted">deg</div>
        </CardCompact>
      </CardSection>

      {/* Playability Status */}
      {stats.playability_score && (
        <div className="mt-4">
          <PlayabilityBadge
            status={getPlayabilityStatus(stats.playability_score)}
            warning={stats.playability_warning}
          />
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-border grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="text-text-secondary">Head Profile:</span>
          <span className={`ml-2 font-medium ${head.profile === 'Distance' ? 'text-purple-400' : head.profile === 'Players' ? 'text-orange-400' : 'text-blue-400'}`}>{head.profile}</span>
        </div>
        <div>
          <span className="text-text-secondary">Shaft Launch:</span>
          <span className={`ml-2 font-medium ${shaft.launch_profile === 'Low' ? 'text-blue-400' : shaft.launch_profile === 'High' ? 'text-orange-400' : 'text-text-primary'}`}>{shaft.launch_profile}</span>
        </div>
      </div>

      {/* Actions Section */}
      <div className="mt-6 pt-4 border-t border-border space-y-3">
        {/* Pricing Row */}
        {(head.street_price || head.msrp || shaft.street_price || shaft.msrp) && (
          <div className="grid grid-cols-3 gap-3">
            {/* Total Build Price - More Prominent */}
            {(head.street_price || head.msrp) && (shaft.street_price || shaft.msrp) && (
              <button
                onClick={() => searchOnSecondSwing(`${head.name} ${shaft.name}`)}
                className="flex flex-col items-center justify-center p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg hover:bg-emerald-500/20 transition-colors"
                title={`Search for ${head.name} + ${shaft.name} on 2nd Swing`}
              >
                <div className="text-xs text-zinc-400 mb-1">Total Build</div>
                <div className="text-2xl font-bold text-emerald-400">
                  ${(head.street_price || head.msrp) + (shaft.street_price || shaft.msrp)}
                </div>
              </button>
            )}
            {/* Head Price */}
            {(head.street_price || head.msrp) && (
              <button
                onClick={() => searchOnSecondSwing(head.name)}
                className="flex flex-col items-center justify-center p-3 bg-zinc-800 border border-zinc-700 rounded-lg hover:bg-zinc-700 transition-colors"
                title={`Search for ${head.name} on 2nd Swing`}
              >
                <div className="text-xs text-zinc-500 mb-1">Head</div>
                <div className="text-lg font-bold text-emerald-400">
                  ${head.street_price || head.msrp}
                </div>
              </button>
            )}
            {/* Shaft Price */}
            {(shaft.street_price || shaft.msrp) && (
              <button
                onClick={() => searchOnSecondSwing(shaft.name)}
                className="flex flex-col items-center justify-center p-3 bg-zinc-800 border border-zinc-700 rounded-lg hover:bg-zinc-700 transition-colors"
                title={`Search for ${shaft.name} on 2nd Swing`}
              >
                <div className="text-xs text-zinc-500 mb-1">Shaft</div>
                <div className="text-lg font-bold text-emerald-400">
                  ${shaft.street_price || shaft.msrp}
                </div>
              </button>
            )}
          </div>
        )}

        {/* Action Buttons Row */}
        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="primary"
            size="lg"
            onClick={onSave}
            disabled={saving}
            icon={saving ? RefreshCw : Save}
            className={`bg-primary text-primary-foreground ${saving ? '[&_svg]:animate-spin' : ''}`}
          >
            Save This Build
          </Button>
          <ShoppingButton head={head} shaft={shaft} />
        </div>
      </div>
    </Card>
  );
}
